(() => {
  if (window.LenchoFormsControllerLoaded) return;
  window.LenchoFormsControllerLoaded = true;

  const CONTACT_SELECTOR = 'form.lcf__form[data-native-contact]';
  const CUSTOM_ORDER_SELECTOR =
    'form.lencho-custom-request__form[data-lco-form], form.lencho-custom-order-form__form[data-lco-form]';
  const NEWSLETTER_SELECTOR = 'form.lencho-footer__newsletter-form';
  const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
  const ALLOWED_IMAGE_EXTENSIONS = /\.(jpe?g|png|webp)$/i;
  const CONTROLLED_FORM_SELECTOR = `${CONTACT_SELECTOR}, ${CUSTOM_ORDER_SELECTOR}, ${NEWSLETTER_SELECTOR}`;

  document.addEventListener(
    'submit',
    (event) => {
      if (event.target instanceof HTMLFormElement && event.target.matches(CONTROLLED_FORM_SELECTOR)) {
        event.preventDefault();
         event.returnValue = false;
      }
    },
    true,
  );

  const value = (form, name) => String(form.elements.namedItem(name)?.value || '').trim();
  const nullable = (input) => {
    const cleaned = String(input || '').trim();
    return cleaned || null;
  };

  const cleanPayload = (payload) =>
    Object.fromEntries(
      Object.entries(payload).filter(([, item]) => {
        if (item === undefined || item instanceof File || item instanceof Element) return false;
        return true;
      }),
    );

  const escapeHtml = (input) =>
    String(input || '').replace(/[&<>"']/g, (char) => {
      const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
      return entities[char] || char;
    });

  const setStatus = (form, message, type = '') => {
    const status = form.querySelector('[data-api-status], [data-lco-status]');
    if (!status) return;
    if (form.matches(CUSTOM_ORDER_SELECTOR)) {
      const submitButton = form.querySelector('[data-lco-submit], button[type="submit"]');
      if (submitButton && submitButton.nextElementSibling !== status) {
        submitButton.insertAdjacentElement('afterend', status);
      }
    }
    const isError = type === 'error';
    const isSuccess = type === 'success';
    const title = isError
      ? status.dataset.errorHeading || form.dataset.errorHeading || 'Please try again'
      : isSuccess
        ? status.dataset.successHeading || form.dataset.successHeading || 'Message received'
        : status.dataset.loadingHeading || form.dataset.loadingHeading || 'Sending';
    const icon = isError ? '!' : isSuccess ? 'OK' : '...';
    status.hidden = false;
    status.innerHTML = `
      <span class="lpf__status-icon" aria-hidden="true">${escapeHtml(icon)}</span>
      <span>
        <strong class="lpf__status-title">${escapeHtml(title)}</strong>
        <span class="lpf__status-message">${escapeHtml(message)}</span>
      </span>
    `;
    status.classList.toggle('is-error', isError);
    status.classList.toggle('is-success', isSuccess);
    status.setAttribute('role', isError ? 'alert' : 'status');
    status.setAttribute('aria-live', isError ? 'assertive' : 'polite');
    status.setAttribute('tabindex', '-1');
    if (isError || isSuccess) {
      requestAnimationFrame(() => status.focus({ preventScroll: true }));
    }
  };

  const setBusy = (form, busy) => {
    const button = form.querySelector('[data-lencho-submit], [data-lco-submit], button[type="submit"]');
    if (!button) return;
    if (!button.dataset.originalHtml) button.dataset.originalHtml = button.innerHTML;
    button.disabled = busy;
    form.setAttribute('aria-busy', String(busy));
    if (busy) {
      button.textContent = form.dataset.sendingText || 'Sending...';
    } else {
      button.innerHTML = button.dataset.originalHtml;
    }
  };

  const safeErrorDetails = async (response, table) => {
    let body = {};
    try {
      body = await response.clone().json();
    } catch (_error) {
      const text = await response.text().catch(() => '');
      body = { message: text };
    }

    console.error('Lencho Supabase error:', {
      table,
      status: response.status,
      code: typeof body?.code === 'string' ? body.code : '',
      message: typeof body?.message === 'string' ? body.message : 'Request failed.',
      details: typeof body?.details === 'string' ? body.details : '',
      hint: typeof body?.hint === 'string' ? body.hint : '',
    });
    return body;
  };

  const post = async (endpoint, options, table) => {
    const response = await fetch(endpoint, options);
    if (!response.ok) {
      const body = await safeErrorDetails(response, table);
      const error = new Error('The request could not be completed.');
      error.responseBody = body;
      throw error;
    }
    if (response.status === 204) return {};
    return response.json().catch(() => ({}));
  };

  const normaliseSupabaseUrl = (input) => {
    const raw = String(input || '').trim().replace(/\/+$/, '');
    if (!raw) return '';

    const directProjectUrl = raw.match(/^https:\/\/([a-z0-9-]+)\.supabase\.co(?:\/.*)?$/i);
    if (directProjectUrl) return `https://${directProjectUrl[1]}.supabase.co`;

    const dashboardUrl = raw.match(/supabase\.com\/dashboard\/project\/([a-z0-9-]+)/i);
    if (dashboardUrl) return `https://${dashboardUrl[1]}.supabase.co`;

    if (/^[a-z0-9-]{15,}$/i.test(raw)) return `https://${raw}.supabase.co`;
    return raw;
  };

  const supabaseConfig = (form) => {
    const baseUrl = normaliseSupabaseUrl(form.dataset.supabaseUrl);
    const publicKey = String(form.dataset.supabaseKey || '').trim();
    return {
      baseUrl,
      publicKey,
      ready: /^https:\/\/[a-z0-9-]+\.supabase\.co$/i.test(baseUrl) && publicKey.length > 20,
    };
  };

  const supabaseSetupMessage = (form, label = 'Form') => {
    const config = supabaseConfig(form);
    const missing = [];
    if (!/^https:\/\/[a-z0-9-]+\.supabase\.co$/i.test(config.baseUrl)) missing.push('Supabase project URL');
    if (config.publicKey.length <= 20) missing.push('Supabase publishable key');
    return `${label} connection is incomplete. Check ${missing.join(' and ') || 'Supabase settings'} in Theme settings > Lencho integrations.`;
  };

  const cloudinaryConfig = (form) => {
    const cloudName = String(form.dataset.cloudinaryCloudName || '').trim();
    const uploadPreset = String(form.dataset.cloudinaryUploadPreset || '').trim();
    return {
      cloudName,
      uploadPreset,
      ready: /^[a-z0-9_-]+$/i.test(cloudName) && uploadPreset.length > 0,
    };
  };

  const uploadToCloudinary = async (form, file) => {
    const config = cloudinaryConfig(form);
    if (!config.ready) {
      throw new Error('Lencho Cloudinary upload is not configured.');
    }
    const body = new FormData();
    body.append('file', file);
    body.append('upload_preset', config.uploadPreset);
    body.append('folder', 'lencho/custom-orders');

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${encodeURIComponent(config.cloudName)}/image/upload`,
      { method: 'POST', body },
    );
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || typeof payload.secure_url !== 'string' || !/^https:\/\//.test(payload.secure_url)) {
      console.error('Lencho Cloudinary upload error:', {
        status: response.status,
        message: typeof payload?.error?.message === 'string' ? payload.error.message : 'Upload failed.',
      });
      throw new Error('Reference image upload failed.');
    }
    return payload.secure_url;
  };

  const postToSupabase = async (form, table, payload) => {
    const config = supabaseConfig(form);
    if (!config.ready) {
      throw new Error('Lencho form storage is not configured.');
    }
    return post(
      `${config.baseUrl}/rest/v1/${table}`,
      {
        method: 'POST',
        headers: {
          apikey: config.publicKey,
          Authorization: `Bearer ${config.publicKey}`,
          'Content-Type': 'application/json',
          Prefer: 'return=minimal',
        },
        body: JSON.stringify(payload),
      },
      table,
    );
  };

  const sendNativeContactCopy = async (form) => {
    const formType = form.elements.namedItem('form_type');
    if (!formType || String(formType.value || '') !== 'contact') return;
    const action = form.getAttribute('action') || window.location.href;
    try {
      const response = await fetch(action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      if (!response.ok) {
        console.warn('Lencho native contact email copy was not accepted:', response.status);
      }
    } catch (error) {
      console.warn('Lencho native contact email copy failed.');
    }
  };

  const sendNativeCustomerCopy = async (form) => {
    const formType = form.elements.namedItem('form_type');
    if (!formType || String(formType.value || '') !== 'customer') return;
    const action = form.getAttribute('action') || window.location.href;
    try {
      const response = await fetch(action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      if (!response.ok) {
        console.warn('Lencho native newsletter email copy was not accepted:', response.status);
      }
    } catch (error) {
      console.warn('Lencho native newsletter email copy failed.');
    }
  };

  const submitPayload = (form, endpoint, table, payload) => {
    if (endpoint) {
      return post(
        endpoint,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
        table,
      );
    }
    return postToSupabase(form, table, payload);
  };

  const resetFormAfterSuccess = (form) => {
    form.reset();
    form.querySelectorAll('[data-lco-file-preview]').forEach((preview) => {
      preview.hidden = true;
      const image = preview.querySelector('img');
      if (image) image.removeAttribute('src');
    });
    form.querySelectorAll('[data-lco-file-meta]').forEach((meta) => {
      meta.hidden = true;
    });
    form.querySelectorAll('[data-lco-file-title]').forEach((title) => {
      title.textContent = '';
    });
    form.querySelectorAll('[data-lco-file-list]').forEach((list) => {
      list.textContent = '';
    });
  };

  const showNewsletterStatus = (form, message, type = 'success') => {
    form.querySelectorAll('.lencho-footer__form-message:not([data-lf-ajax-status]):not([data-lf-privacy-error])').forEach((node) => {
      node.remove();
    });
    let status = form.querySelector('[data-lf-ajax-status]');
    if (!status) {
      status = document.createElement('p');
      status.setAttribute('data-lf-ajax-status', '');
      status.className = 'lencho-footer__form-message';
      const privacyError = form.querySelector('[data-lf-privacy-error]');
      if (privacyError) privacyError.insertAdjacentElement('afterend', status);
      else form.appendChild(status);
    }
    status.hidden = false;
    status.textContent = message;
    status.classList.toggle('lencho-footer__form-message--success', type === 'success');
    status.classList.toggle('lencho-footer__form-message--error', type === 'error');
    status.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };

  const contactPayload = (form) => {
    const selectedDistrict = value(form, 'contact[District]');
    const manualDistrict = value(form, 'contact[District name]');
    return cleanPayload({
      name: value(form, 'contact[Full name]'),
      email: value(form, 'contact[email]'),
      phone: nullable(value(form, 'contact[Phone]')),
      subject: value(form, 'contact[Enquiry type]'),
      order_number: nullable(value(form, 'contact[Order number]')),
      pincode: value(form, 'contact[Pincode]'),
      state: value(form, 'contact[State]'),
      district: selectedDistrict === 'Other' ? manualDistrict : selectedDistrict || manualDistrict,
      message: value(form, 'contact[body]'),
      status: 'New',
      source: 'Lencho Contact Page',
    });
  };

  const customOrderPayload = (form) => {
    const additionalDetails = [
      ['Occasion', value(form, 'contact[Occasion]')],
      ['Preferred colors', value(form, 'contact[Preferred colors]')],
      ['Approximate budget (INR)', value(form, 'contact[Budget]')],
      ['Required-by date', value(form, 'contact[Required-by date]')],
      ['Reference URL', value(form, 'contact[Reference URL]')],
    ]
      .filter(([, item]) => item)
      .map(([label, item]) => `${label}: ${item}`)
      .join('\n');
    const description = [
      value(form, 'contact[What should we make]'),
      additionalDetails ? `Additional custom-order details:\n${additionalDetails}` : '',
    ]
      .filter(Boolean)
      .join('\n\n');

    return cleanPayload({
      name: value(form, 'contact[Full name]'),
      email: value(form, 'contact[email]'),
      phone: value(form, 'contact[Phone or WhatsApp]'),
      product_type: value(form, 'contact[Product type]'),
      description,
      quantity: Number.parseInt(value(form, 'contact[Quantity]'), 10),
      pincode: value(form, 'contact[Pincode]'),
      state: value(form, 'contact[State]'),
      district: value(form, 'contact[District]'),
      address_notes: nullable(value(form, 'contact[Address or delivery notes]')),
      message: value(form, 'contact[body]'),
      image1: null,
      image2: null,
      image3: null,
      status: 'Pending',
      consent: true,
      source: 'Lencho Custom Order Page',
    });
  };

  const newsletterPayload = (form) =>
    cleanPayload({
      email: value(form, 'contact[email]'),
      status: 'Active',
    });

  const selectedFiles = (form) =>
    [...form.querySelectorAll('[data-lco-file]')]
      .map((input) => input.files?.[0])
      .filter(Boolean);

  const validateCustomOrder = (form) => {
    const quantity = Number.parseInt(value(form, 'contact[Quantity]'), 10);
    const pincode = value(form, 'contact[Pincode]');
    const consent = form.elements.namedItem('contact[Consent]');
    const files = selectedFiles(form);
    const maxFiles = Number(form.dataset.maxFileCount || 3);
    const maxBytes = Number(form.dataset.maxFileBytes || 5 * 1024 * 1024);

    if (!Number.isInteger(quantity) || quantity < 1) {
      setStatus(form, 'Enter a valid quantity of at least 1.', 'error');
      return false;
    }
    if (!/^\d{6}$/.test(pincode)) {
      setStatus(form, 'Enter a valid six-digit pincode.', 'error');
      return false;
    }
    if (!consent?.checked) {
      setStatus(form, 'Please accept the consent checkbox before submitting.', 'error');
      consent?.focus();
      return false;
    }
    if (files.length > maxFiles) {
      setStatus(form, `Please select no more than ${maxFiles} images.`, 'error');
      return false;
    }
    const invalid = files.find(
      (file) => !(ALLOWED_IMAGE_TYPES.has(file.type) || ALLOWED_IMAGE_EXTENSIONS.test(file.name)),
    );
    if (invalid) {
      setStatus(form, 'Please select JPG, PNG or WEBP images only.', 'error');
      return false;
    }
    if (files.some((file) => file.size > maxBytes)) {
      setStatus(form, `Each image must be ${Math.round(maxBytes / 1048576)} MB or smaller.`, 'error');
      return false;
    }
    return true;
  };

  const initContact = (form) => {
    if (!form || form.dataset.lenchoSubmissionReady === 'true') return;
    form.dataset.lenchoSubmissionReady = 'true';
    form.addEventListener('submit', async (event) => {

      event.preventDefault();
          event.returnValue = false;
      const endpoint = form.dataset.apiEndpoint?.trim();
      if (!form.reportValidity() || form.getAttribute('aria-busy') === 'true') return;
      if (!endpoint && !supabaseConfig(form).ready) {
        setStatus(
          form,
          supabaseSetupMessage(form, 'Contact form'),
          'error',
        );
        return;
      }
      if (value(form, 'website')) {
        setStatus(form, form.dataset.successText || 'Thank you. Your message has been received.', 'success');
        return;
      }

      const payload = contactPayload(form);
      if (!/^\d{6}$/.test(payload.pincode)) {
        setStatus(form, 'Enter a valid six-digit pincode.', 'error');
        return;
      }

      setBusy(form, true);
      setStatus(form, form.dataset.sendingText || 'Sending...');
      try {
        await submitPayload(form, endpoint, 'contact_requests', payload);
        setStatus(form, form.dataset.successText || 'Thank you. Your message has been received.', 'success');
        resetFormAfterSuccess(form);
      } catch (_error) {
        setStatus(form, form.dataset.errorText || 'Your message could not be sent. Please try again.', 'error');
      } finally {
        setBusy(form, false);
      }
    });
  };

  const initCustomOrder = (form) => {
    if (!form || form.dataset.lenchoSubmissionReady === 'true') return;
    form.dataset.lenchoSubmissionReady = 'true';
    form.addEventListener('submit', async (event) => {
  event.preventDefault();
      event.returnValue = false;
      const endpoint = form.dataset.endpoint?.trim();
      if (!form.reportValidity() || form.getAttribute('aria-busy') === 'true') return;
      if (!endpoint && !supabaseConfig(form).ready) {
        setStatus(
          form,
          supabaseSetupMessage(form, 'Custom order form'),
          'error',
        );
        return;
      }
      if (value(form, 'website')) {
        setStatus(form, form.dataset.successText || 'Thank you. Your custom request has been received.', 'success');
        return;
      }
      if (!validateCustomOrder(form)) return;

      const payload = customOrderPayload(form);
      const files = selectedFiles(form);
      const canUseEndpointUpload = files.length && endpoint && form.dataset.multipart === 'true';
      const canUseCloudinaryUpload = files.length && cloudinaryConfig(form).ready;
      if (files.length && !canUseEndpointUpload && !canUseCloudinaryUpload) {
        setStatus(
          form,
          'Reference image upload is not configured. Add Cloudinary cloud name and unsigned upload preset in Theme settings, or submit without images.',
          'error',
        );
        return;
      }

      setBusy(form, true);
      setStatus(form, form.dataset.sendingText || 'Sending request...');
      try {
        if (canUseEndpointUpload) {
          if (!endpoint) {
            throw new Error('Reference images require a secure upload endpoint.');
          }
          const body = new FormData();
          Object.entries(payload).forEach(([key, item]) => {
            if (item !== null) body.append(key, String(item));
          });
          files.forEach((file, index) => body.append(`reference_photo_${index + 1}`, file, file.name));
          const options = { method: 'POST', body };
          const result = await post(endpoint, options, 'custom_orders');
          const imageUrls = Array.isArray(result.image_urls)
            ? result.image_urls.filter((url) => typeof url === 'string' && /^https:\/\//.test(url))
            : [];
          if (imageUrls.length !== files.length) {
            throw new Error('Secure image upload did not return every image URL.');
          }
        } else {
          if (canUseCloudinaryUpload) {
            setStatus(form, 'Uploading reference images...');
            const imageUrls = await Promise.all(files.map((file) => uploadToCloudinary(form, file)));
            payload.image1 = imageUrls[0] || null;
            payload.image2 = imageUrls[1] || null;
            payload.image3 = imageUrls[2] || null;
            setStatus(form, form.dataset.sendingText || 'Sending request...');
          }
          await submitPayload(form, endpoint, 'custom_orders', payload);
        }
        setStatus(
          form,
          form.dataset.successText ||
            'Thank you. Your custom request has been received. Lencho will contact you after checking the details.',
          'success',
        );
        resetFormAfterSuccess(form);
      } catch (_error) {
        setStatus(form, form.dataset.errorText || 'Your request could not be sent. Please try again.', 'error');
      } finally {
        setBusy(form, false);
      }
    });
  };

  const initNewsletter = (form) => {
    if (!form || form.dataset.lenchoAjaxReady === 'true') return;
    form.dataset.lenchoAjaxReady = 'true';
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const privacyCheckbox = form.querySelector('[data-lf-privacy-checkbox][required]');
      const privacyMessage = form.querySelector('[data-lf-privacy-error]');
      if (privacyCheckbox && !privacyCheckbox.checked) {
        if (privacyMessage) privacyMessage.hidden = false;
        showNewsletterStatus(form, privacyMessage?.textContent?.trim() || 'Please accept the Privacy Policy before subscribing.', 'error');
        privacyCheckbox.focus();
        return;
      }
      if (privacyMessage) privacyMessage.hidden = true;
      if (!form.reportValidity() || form.getAttribute('aria-busy') === 'true') return;
      const endpoint = form.dataset.newsletterEndpoint?.trim();
      if (!endpoint && !supabaseConfig(form).ready) {
        showNewsletterStatus(
          form,
          supabaseSetupMessage(form, 'Newsletter'),
          'error',
        );
        return;
      }
      const button = form.querySelector('button[type="submit"]');
      const originalHtml = button?.innerHTML;
      if (button) button.disabled = true;
      form.setAttribute('aria-busy', 'true');
      try {
        await submitPayload(form, endpoint, 'newsletter_subscribers', newsletterPayload(form));
        form.reset();
        showNewsletterStatus(form, form.dataset.successText || 'Thank you for joining Lencho.', 'success');
      } catch (_error) {
        showNewsletterStatus(form, form.dataset.errorText || 'Please enter a valid email address.', 'error');
      } finally {
        if (button) {
          button.disabled = false;
          if (originalHtml) button.innerHTML = originalHtml;
        }
        form.setAttribute('aria-busy', 'false');
      }
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(CONTACT_SELECTOR).forEach(initContact);
    scope.querySelectorAll(CUSTOM_ORDER_SELECTOR).forEach(initCustomOrder);
    scope.querySelectorAll(NEWSLETTER_SELECTOR).forEach(initNewsletter);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
  window.addEventListener('load', () => initAll(), { once: true });
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
